// pages/OperatorClients.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Edit, Save, Plus, RotateCw, X, ChevronDown, ChevronRight, Copy, Trash, Eye, EyeOff, Ticket
} from "lucide-react";
import OperatorCardActions from "../components/OperatorCardActions";
import FilterPopover from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import EditFloatingActions from "../components/EditFloatingActions";
import {
    fetchClients, addClient, updateClient, fetchClientAccessInvites, fetchClientAccessRequests, approveClientAccessRequest, rejectClientAccessRequest, sendClientAccessRequestMessage, createClientAccessInvite, revokeClientAccessInvite
} from "../services/clients";
import {
    fetchClientMultiLocations, addClientMultiLocation, updateClientMultiLocation, deleteClientMultiLocation
} from "../services/clients_multi_locations";
import { fetchLocations } from "../services/locations";
import {
    fetchNetworkRestrictions,
    fetchOperatorComplaints,
    resolveOperatorComplaint,
} from "../services/clientRestrictions";
import ClientRestrictionPanel from "../components/ClientRestrictionPanel";

const phoneRegex = /^(\+?38)?0\d{9}$/;
const formatPhone = (phone) => {
    let digits = (phone || "").replace(/\D/g, "");
    if (!digits) digits = "";
    if (!digits.startsWith("38") && digits.length > 0) digits = "38" + digits;
    return digits ? "+" + digits : "";
};

const toLocalDateTimeInputValue = (date) => {
    const value = date instanceof Date ? date : new Date(date);
    const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

const ClientsOperators = ({ onPendingAccessRequestsChange }) => {
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [locations, setLocations] = useState([]);
    const [message, setMessage] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [editingId, setEditingId] = useState(null);
    const [edited, setEdited] = useState({});
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ apartment: "", location: "", status: "", ban: "" });
    const [bannedClientIds, setBannedClientIds] = useState(() => new Set());
    const [showAddModal, setShowAddModal] = useState(false);
    const [newClient, setNewClient] = useState({
        name: "", email: "", phone: "", password: "", descriptions: "",
        location_id: "", apartment: "", personal_account: "", car_number: ""
    });
    const [hasMultiLocation, setHasMultiLocation] = useState({});
    const [expandedClientId, setExpandedClientId] = useState(null);
    const [multiLocations, setMultiLocations] = useState({});
    const [editingMultiId, setEditingMultiId] = useState(null);
    const [editedMulti, setEditedMulti] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [expandedClientCardId, setExpandedClientCardId] = useState(null);
    const [showNewClientPassword, setShowNewClientPassword] = useState(false);
    const [showEditedPassword, setShowEditedPassword] = useState(false);
    const [accessRequests, setAccessRequests] = useState([]);
    const [accessInvites, setAccessInvites] = useState([]);
    const [requestForms, setRequestForms] = useState({});
    const [highlightedAccessRequestId, setHighlightedAccessRequestId] = useState(null);
    const [sendingRequestMessageId, setSendingRequestMessageId] = useState(null);
    const [creatingInvite, setCreatingInvite] = useState(false);
    const [showInvitesPanel, setShowInvitesPanel] = useState(false);
    const [showArchivedInvites, setShowArchivedInvites] = useState(false);
    const [inviteForm, setInviteForm] = useState({
        expires_at: toLocalDateTimeInputValue(new Date(Date.now() + 180 * 24 * 60 * 60 * 1000)),
    });
    const [complaints, setComplaints] = useState([]);
    const [complaintsOpenCount, setComplaintsOpenCount] = useState(0);
    const [complaintForms, setComplaintForms] = useState({});
    const [resolvingComplaintId, setResolvingComplaintId] = useState(null);
    const [expandedRestrictionClientId, setExpandedRestrictionClientId] = useState(null);
    const approvalsRef = useRef(null);

    const loadClients = useCallback(async () => {
        try {
            const data = await fetchClients();
            setClients(data);
            setFilteredClients(data);
            // перевірити наявність мульти-локацій + кешувати для фільтрів (паралельно)
            const multiData = {};
            const multiCache = {};
            await Promise.all(
                data.map(async (client) => {
                    try {
                        const ml = await fetchClientMultiLocations(client.id);
                        const list = Array.isArray(ml) ? ml : [];
                        multiData[client.id] = list.length > 0;
                        multiCache[client.id] = list;
                    } catch {
                        multiData[client.id] = false;
                        multiCache[client.id] = [];
                    }
                })
            );
            setHasMultiLocation(multiData);
            setMultiLocations(multiCache);
        } catch (error) {
            setMessage(`❌ Помилка: ${error?.message || error}`);
        }
    }, []);

    const loadComplaints = useCallback(async () => {
        try {
            const data = await fetchOperatorComplaints("open");
            setComplaints(Array.isArray(data?.items) ? data.items : []);
            setComplaintsOpenCount(Number(data?.open_count) || 0);
        } catch (error) {
            setComplaints([]);
            setComplaintsOpenCount(0);
        }
    }, []);

    const loadBannedClients = useCallback(async () => {
        try {
            const data = await fetchNetworkRestrictions();
            const ids = new Set();
            (Array.isArray(data?.items) ? data.items : []).forEach((item) => {
                const muted = Boolean(item?.chat_muted);
                const modules = Array.isArray(item?.readonly_modules) ? item.readonly_modules : [];
                if (muted || modules.length > 0) {
                    ids.add(Number(item.client_id));
                }
            });
            setBannedClientIds(ids);
        } catch (_) {
            setBannedClientIds(new Set());
        }
    }, []);

    const loadLocations = useCallback(async () => {
        try {
            const data = await fetchLocations();
            setLocations(data);
        } catch (error) {
            setMessage(`❌ Помилка: ${error?.message || error}`);
        }
    }, []);

    const loadAccessRequests = useCallback(async () => {
        try {
            const data = await fetchClientAccessRequests();
            const items = Array.isArray(data) ? data : [];
            setAccessRequests(items);
            if (typeof onPendingAccessRequestsChange === "function") {
                onPendingAccessRequestsChange(items.length);
            }
            window.dispatchEvent(new CustomEvent("wr_operator_client_access_requests_changed", {
                detail: { count: items.length },
            }));
            setRequestForms((prev) => {
                const next = { ...prev };
                items.forEach((item) => {
                    if (!next[item.id]) {
                        next[item.id] = {
                            location_id: item.location_id ? String(item.location_id) : "",
                            apartment: item.apartment || "",
                            personal_account: item.personal_account || "",
                            review_comment: "",
                            message_body: "",
                        };
                    }
                });
                return next;
            });
        } catch (error) {
            setMessage(`❌ ${error?.message || error}`);
        }
    }, [onPendingAccessRequestsChange]);

    const loadAccessInvites = useCallback(async () => {
        try {
            const data = await fetchClientAccessInvites();
            setAccessInvites(Array.isArray(data) ? data : []);
        } catch (error) {
            setMessage(`❌ ${error?.message || error}`);
        }
    }, []);

    useEffect(() => {
        loadClients();
        loadLocations();
        loadAccessRequests();
        loadAccessInvites();
        loadComplaints();
        loadBannedClients();

        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [loadClients, loadLocations, loadAccessInvites, loadAccessRequests, loadComplaints, loadBannedClients]);

    const handleResolveComplaint = async (complaintId, status) => {
        const form = complaintForms[complaintId] || {};
        setResolvingComplaintId(complaintId);
        try {
            const muteDays = form.mute_days ? Number(form.mute_days) : null;
            await resolveOperatorComplaint(complaintId, {
                status,
                resolution_note: (form.resolution_note || "").trim() || null,
                mute_accused_days: muteDays || null,
                escalate_strike: true,
            });
            setMessage(status === "resolved" ? "✅ Скаргу опрацьовано" : "Скаргу відхилено");
            await Promise.all([loadComplaints(), loadBannedClients()]);
        } catch (e) {
            setMessage(`❌ ${e?.message || e}`);
        } finally {
            setResolvingComplaintId(null);
        }
    };

    useEffect(() => {
        let raw = null;
        try {
            raw = localStorage.getItem("pendingClientAccessRequestOpenId_operator");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const id = parseInt(raw, 10);
        if (!Number.isFinite(id) || id <= 0) {
            try {
                localStorage.removeItem("pendingClientAccessRequestOpenId_operator");
            } catch (_) {
                // ignore
            }
            return;
        }

        const item = (accessRequests || []).find((request) => Number(request?.id) === id);
        if (!item) return;

        try {
            localStorage.removeItem("pendingClientAccessRequestOpenId_operator");
        } catch (_) {
            // ignore
        }

        setHighlightedAccessRequestId(id);
        window.requestAnimationFrame(() => {
            approvalsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });

        const timer = window.setTimeout(() => {
            setHighlightedAccessRequestId((prev) => (prev === id ? null : prev));
        }, 60000);

        return () => window.clearTimeout(timer);
    }, [accessRequests]);

    const handleApproveAccessRequest = async (requestId) => {
        const form = requestForms[requestId] || {};
        try {
            await approveClientAccessRequest(requestId, {
                location_id: form.location_id ? parseInt(form.location_id) : null,
                apartment: form.apartment || null,
                personal_account: form.personal_account || null,
                review_comment: form.review_comment || null,
            });
            await Promise.all([loadAccessRequests(), loadClients()]);
            setMessage("✅ Запит клієнта підтверджено");
        } catch (error) {
            setMessage(`❌ ${error?.message || error}`);
        }
    };

    const handleCreateInvite = async () => {
        if (!inviteForm.expires_at) {
            setMessage("❌ Вкажіть кінцеву дату інвайт-коду");
            return;
        }

        setCreatingInvite(true);
        try {
            const created = await createClientAccessInvite({
                expires_at: new Date(inviteForm.expires_at).toISOString(),
            });
            await loadAccessInvites();
            setMessage(`✅ Інвайт-код ${created.code} створено`);
        } catch (error) {
            setMessage(`❌ ${error?.message || error}`);
        } finally {
            setCreatingInvite(false);
        }
    };

    const handleCopyInviteCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setMessage("✅ Інвайт-код скопійовано");
        } catch (_) {
            setMessage(`ℹ️ Інвайт-код: ${code}`);
        }
    };

    const handleRevokeInvite = async (inviteId) => {
        if (!window.confirm("Відкликати цей інвайт-код?")) return;
        try {
            await revokeClientAccessInvite(inviteId);
            await loadAccessInvites();
            setMessage("✅ Інвайт-код відкликано");
        } catch (error) {
            setMessage(`❌ ${error?.message || error}`);
        }
    };

    const handleRejectAccessRequest = async (requestId) => {
        const form = requestForms[requestId] || {};
        try {
            await rejectClientAccessRequest(requestId, {
                review_comment: form.review_comment || null,
            });
            await loadAccessRequests();
            setMessage("✅ Запит клієнта відхилено");
        } catch (error) {
            setMessage(`❌ ${error?.message || error}`);
        }
    };

    const handleSendAccessRequestMessage = async (requestId) => {
        const form = requestForms[requestId] || {};
        const body = (form.message_body || "").trim();
        if (!body) {
            setMessage("❌ Введіть коментар для клієнта");
            return;
        }

        setSendingRequestMessageId(requestId);
        try {
            await sendClientAccessRequestMessage(requestId, body);
            setRequestForms((prev) => ({
                ...prev,
                [requestId]: {
                    ...(prev[requestId] || {}),
                    message_body: "",
                },
            }));
            await loadAccessRequests();
            setMessage("✅ Коментар надіслано клієнту");
        } catch (error) {
            setMessage(`❌ ${error?.message || error}`);
        } finally {
            setSendingRequestMessageId(null);
        }
    };

    const loadMultiLocations = async (clientId) => {
        try {
            const data = await fetchClientMultiLocations(clientId);
            const list = Array.isArray(data) ? data : [];
            setMultiLocations((prev) => ({ ...prev, [clientId]: list }));
            setHasMultiLocation((prev) => ({ ...prev, [clientId]: list.length > 0 }));
            return list;
        } catch (e) {
            setMessage(`❌ ${e?.message || e}`);
            return [];
        }
    };

    const toggleExpand = (clientId) => {
        if (expandedClientId === clientId) {
            setExpandedClientId(null);
        } else {
            setExpandedClientId(clientId);
            setExpandedClientCardId(clientId);
            loadMultiLocations(clientId);
        }
    };

    // --- Пошук та фільтри ---
    const handleSearch = (value) => {
        setSearch(value);
        applyFilters(value, filters);
    };

    const handleFilterChange = (field, value) => {
        const updated = { ...filters, [field]: value };
        // каскадність: Локація -> Квартира
        if (field === "location") {
            updated.apartment = "";
        }
        setFilters(updated);
        applyFilters(search, updated);
    };

    const getLocationLabelById = (locationId) => {
        if (locationId === null || locationId === undefined || locationId === "") return "";
        const id = parseInt(locationId);
        const loc = locations.find((l) => l.id === id);
        if (!loc) return "";
        return [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ");
    };

    const getAddressRowsForClient = (client) => {
        const rows = [
            {
                apartment: client.apartment ?? "",
                location_id: client.location_id ?? null,
            },
        ];
        const extra = multiLocations[client.id] || [];
        for (const ml of extra) {
            rows.push({
                apartment: ml.apartment ?? "",
                location_id: ml.location_id ?? null,
                personal_account: ml.personal_account ?? "",
            });
        }
        return rows;
    };

    const getAllAddressRows = () => {
        const rows = [];
        for (const client of clients) {
            rows.push(...getAddressRowsForClient(client));
        }
        return rows;
    };

    const getLocationOptionsUsedByClients = () => {
        const rows = getAllAddressRows();
        const ids = new Set();
        let hasNull = false;
        for (const r of rows) {
            if (r.location_id === null || r.location_id === undefined || r.location_id === "") {
                hasNull = true;
            } else {
                ids.add(parseInt(r.location_id));
            }
        }
        const opts = locations
            .filter((loc) => ids.has(loc.id))
            .sort((a, b) => {
                const la = [a.street, a.house_number, a.location_name].filter(Boolean).join(" ").toLowerCase();
                const lb = [b.street, b.house_number, b.location_name].filter(Boolean).join(" ").toLowerCase();
                return la.localeCompare(lb, undefined, { numeric: true });
            });
        return { opts, hasNull };
    };

    const getApartmentOptions = (locationValue) => {
        const rows = getAllAddressRows();
        const apartments = new Set();
        const isNullLocation = locationValue === "null";
        const hasLocationFilter = !!locationValue;
        const locationId = hasLocationFilter && !isNullLocation ? parseInt(locationValue) : null;

        for (const r of rows) {
            if (hasLocationFilter) {
                if (isNullLocation) {
                    if (!(r.location_id === null || r.location_id === undefined || r.location_id === "")) continue;
                } else {
                    if ((r.location_id ?? null) === null) continue;
                    if (parseInt(r.location_id) !== locationId) continue;
                }
            }

            const apt = (r.apartment || "").toString();
            if (apt) apartments.add(apt);
        }

        return [...apartments].sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { numeric: true }));
    };

    const applyFilters = (searchValue, filterValues) => {
        let result = [...clients];
        const lower = (searchValue || "").toString().toLowerCase().trim();

        result = result.filter((client) => {
            const addressRows = getAddressRowsForClient(client);

            const searchMatch = !lower
                ? true
                : (
                    (client.name || "").toLowerCase().includes(lower) ||
                    (client.email || "").toLowerCase().includes(lower) ||
                    (client.phone || "").toString().toLowerCase().includes(lower) ||
                    (client.personal_account || "").toString().toLowerCase().includes(lower) ||
                    addressRows.some((r) =>
                        (r.apartment || "").toString().toLowerCase().includes(lower) ||
                        (r.personal_account || "").toString().toLowerCase().includes(lower) ||
                        getLocationLabelById(r.location_id).toLowerCase().includes(lower)
                    )
                );

            const hasAnyRowFilters = !!(filterValues.location || filterValues.apartment);

            const rowMatch = !hasAnyRowFilters
                ? true
                : addressRows.some((r) => {
                    if (filterValues.apartment && (r.apartment || "") !== filterValues.apartment) return false;

                    if (filterValues.location) {
                        if (filterValues.location === "null") {
                            return r.location_id === null || r.location_id === undefined || r.location_id === "";
                        }
                        if (r.location_id === null || r.location_id === undefined || r.location_id === "") return false;
                        return parseInt(r.location_id) === parseInt(filterValues.location);
                    }

                    return true;
                });

            const statusMatch = !filterValues.status
                ? true
                : filterValues.status === "active"
                    ? Boolean(client.status)
                    : !client.status;

            const banMatch = !filterValues.ban
                ? true
                : filterValues.ban === "banned"
                    ? bannedClientIds.has(Number(client.id))
                    : !bannedClientIds.has(Number(client.id));

            return searchMatch && rowMatch && statusMatch && banMatch;
        });

        setFilteredClients(result);
    };

    useEffect(() => {
        applyFilters(search, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clients, multiLocations, bannedClientIds]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredClients) return [];
        const sorted = [...filteredClients];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    // --- Редагування клієнта ---
    const handleEdit = (client) => {
        setEditingId(client.id);
        setExpandedClientCardId(client.id);
        setEdited({
            name: client.name ?? "",
            email: client.email ?? "",
            phone: client.phone ?? "",
            password: "",
            descriptions: client.descriptions ?? "",
            status: !!client.status,
            car_number: client.car_number ?? "",
            location_id: client.location_id ?? "",
            apartment: client.apartment ?? "",
            personal_account: client.personal_account ?? "",
        });
    };

    const toggleClientCardExpanded = (clientId, isEditing) => {
        if (isEditing) return;
        setExpandedClientCardId((prev) => (prev === clientId ? null : clientId));
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEdited({});
    };

    const handleSave = async (id) => {
        try {
            if (edited.phone && !phoneRegex.test(edited.phone)) {
                alert("Неправильний формат телефону");
                return;
            }
            const payload = { ...edited };
            if (payload.phone !== undefined) {
                const trimmed = (payload.phone || "").toString().trim();
                payload.phone = trimmed ? formatPhone(trimmed) : null;
            }
            if (!payload.password || !payload.password.trim()) delete payload.password;
            // ensure location_id is null or int
            payload.location_id = payload.location_id === "" ? null : parseInt(payload.location_id);
            await updateClient(id, payload);
            await loadClients();
            setEditingId(null);
        } catch (error) {
            setMessage(`❌ Помилка збереження: ${error?.message || error}`);
        }
    };

    // --- Додавання нового клієнта ---
    const handleAddClient = async () => {
        try {
            if (!newClient.name || !newClient.email || !newClient.password) {
                alert("Ім'я, email і пароль обов'язкові!");
                return;
            }
            if (newClient.phone && !phoneRegex.test(newClient.phone)) {
                alert("Невірний формат телефону");
                return;
            }

            const payload = {
                ...newClient,
                phone: newClient.phone ? formatPhone(newClient.phone) : null,
                location_id: newClient.location_id === "" ? null : parseInt(newClient.location_id),
            };

            await addClient(payload);
            setShowAddModal(false);
            setNewClient({
                name: "", email: "", phone: "", password: "", descriptions: "",
                location_id: "", apartment: "", personal_account: "", car_number: ""
            });
            await loadClients();
            setMessage("✅ Клієнта додано");
        } catch (error) {
            setMessage(`❌ Помилка додавання: ${error?.message || error}`);
        }
    };

    // --- Мульти-локації: редагування / додавання / копіювання / видалення ---
    const handleEditMulti = (ml) => {
        setEditingMultiId(ml.id);
        setEditedMulti({
            location_id: ml.location_id ?? "",
            descriptions: ml.descriptions ?? "",
            status: !!ml.status,
            apartment: ml.apartment ?? "",
            personal_account: ml.personal_account ?? "",
        });
    };

    const handleCancelMulti = () => {
        setEditingMultiId(null);
        setEditedMulti({});
    };

    const handleSaveMulti = async (mlId, clientId, source) => {
        try {
            const payload = {
                ...editedMulti,
                location_id: editedMulti.location_id === "" ? null : parseInt(editedMulti.location_id),
            };
            await updateClientMultiLocation(mlId, payload, source || null);
            setEditingMultiId(null);
            await loadMultiLocations(clientId);
            setMessage("✅ Локацію збережено");
        } catch (e) {
            setMessage(`❌ ${e?.message || e}`);
        }
    };

    const handleAddMulti_location = async (clientId, initial = {}) => {
        try {
            const payload = {
                location_id: initial.location_id ?? null,
                descriptions: initial.descriptions ?? "",
                status: initial.status ?? false,
                apartment: initial.apartment ?? null,
                personal_account: initial.personal_account ?? null,
            };
            await addClientMultiLocation(clientId, payload);

            // Спочатку розгорнути, потім завантажити
            setHasMultiLocation((prev) => ({ ...prev, [clientId]: true }));
            setExpandedClientId(clientId);
            await loadMultiLocations(clientId);
            setMessage("✅ Додано локацію");
        } catch (e) {
            setMessage(`❌ ${e?.message || e}`);
        }
    };

    const handleCopy = async (client) => {
        // Створюємо чернетку нової локації, а не активний дубль основної адреси.
        try {
            const initial = {
                location_id: null,
                descriptions: client.descriptions ?? "",
                status: false,
                apartment: null,
                personal_account: null,
            };
            await handleAddMulti_location(client.id, initial);
        } catch (e) {
            setMessage(`❌ ${e?.message || e}`);
        }
    };

    const handleDeleteMulti = async (mlId, clientId, source) => {
        if (!window.confirm("Видалити цю локацію?")) return;
        try {
            await deleteClientMultiLocation(mlId, source || null);
            await loadMultiLocations(clientId);
            setMessage("✅ Локацію видалено");
        } catch (e) {
            setMessage(`❌ ${e?.message || e}`);
        }
    };

    const renderMobileField = (label, content) => (
        <div style={{ marginBottom: 6, lineHeight: 1.25 }}>
            <strong>{label}</strong> {content}
        </div>
    );

    const visibleInvites = accessInvites.filter((invite) => {
        const isRevoked = invite.status === false;
        return showArchivedInvites ? true : !isRevoked;
    });

    // --- UI рендер ---
    return (
        <div className="operator-clients-container">
            <div className="config-header">
                <div>
                    <h2>Клієнти</h2>
                </div>
                <div className="header-buttons operator-clients-header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={() => { loadClients(); loadAccessRequests(); loadAccessInvites(); loadComplaints(); loadBannedClients(); setMessage(""); }} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <FilterPopover
                        active={Boolean(filters.status || filters.location || filters.apartment || filters.ban)}
                        title="Фільтри клієнтів"
                        buttonTitle="Фільтри клієнтів"
                        onClear={() => {
                            const cleared = { apartment: "", location: "", status: "", ban: "" };
                            setFilters(cleared);
                            applyFilters(search, cleared);
                        }}
                    >
                        <label className="toolbar-filter-field">
                            <span>Статус</span>
                            <select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                                <option value="">Усі</option>
                                <option value="active">Активні</option>
                                <option value="inactive">Неактивні</option>
                            </select>
                        </label>
                        <label className="toolbar-filter-field">
                            <span>Обмеження</span>
                            <select value={filters.ban || ""} onChange={(e) => handleFilterChange("ban", e.target.value)}>
                                <option value="">Усі</option>
                                <option value="banned">У бані / з обмеженнями ({bannedClientIds.size})</option>
                                <option value="not_banned">Без обмежень</option>
                            </select>
                        </label>
                        <label className="toolbar-filter-field">
                            <span>Локація</span>
                            <select value={filters.location} onChange={(e) => handleFilterChange("location", e.target.value)}>
                                <option value="">Усі</option>
                                {(() => {
                                    const { opts, hasNull } = getLocationOptionsUsedByClients();
                                    return (
                                        <>
                                            {hasNull && <option value="null">Без локації</option>}
                                            {opts.map((loc) => (
                                                <option key={loc.id} value={loc.id}>
                                                    {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                                </option>
                                            ))}
                                        </>
                                    );
                                })()}
                            </select>
                        </label>
                        <label className="toolbar-filter-field">
                            <span>Квартира</span>
                            <select value={filters.apartment} onChange={(e) => handleFilterChange("apartment", e.target.value)}>
                                <option value="">Усі</option>
                                {getApartmentOptions(filters.location).map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </label>
                    </FilterPopover>
                    <ToolbarSearch
                        value={search}
                        onChange={handleSearch}
                        placeholder="Пошук клієнтів"
                    />
                    <button
                        type="button"
                        className={`add-btn refresh toolbar-icon-btn operator-clients-invites-btn ${showInvitesPanel ? "is-active" : ""}`}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            setShowInvitesPanel((prev) => !prev);
                        }}
                        onPointerUp={(e) => e.currentTarget.blur()}
                        title="Інвайт-коди"
                        aria-label="Інвайт-коди"
                        aria-expanded={showInvitesPanel}
                    >
                        <Ticket size={16} />
                    </button>
                </div>
            </div>

            {message && <p className="message">{message}</p>}

            {complaints.length > 0 && (
                <div className="config-card" style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        <strong>Скарги клієнтів: {complaintsOpenCount || complaints.length}</strong>
                        <button type="button" className="small-btn" onClick={loadComplaints}>Оновити</button>
                    </div>
                    <div style={{ display: "grid", gap: 12 }}>
                        {complaints.map((c) => (
                            <div key={c.id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, background: "#fff" }}>
                                <div style={{ fontSize: 14, fontWeight: 650, marginBottom: 4 }}>
                                    {c.reporter_name} → {c.accused_name}
                                </div>
                                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                                    {c.created_at ? new Date(c.created_at).toLocaleString("uk-UA") : ""}
                                    {c.head_id ? ` · чат #${c.head_id}` : ""}
                                </div>
                                <div style={{ marginBottom: 10, whiteSpace: "pre-wrap" }}>{c.comment}</div>
                                <textarea
                                    rows={2}
                                    placeholder="Коментар оператора / причина бану"
                                    value={complaintForms[c.id]?.resolution_note || ""}
                                    onChange={(e) => setComplaintForms((prev) => ({
                                        ...prev,
                                        [c.id]: { ...(prev[c.id] || {}), resolution_note: e.target.value },
                                    }))}
                                    style={{ width: "100%", marginBottom: 8, borderRadius: 8, border: "1px solid #cbd5e1", padding: 8 }}
                                />
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                                    <select
                                        value={complaintForms[c.id]?.mute_days || ""}
                                        onChange={(e) => setComplaintForms((prev) => ({
                                            ...prev,
                                            [c.id]: { ...(prev[c.id] || {}), mute_days: e.target.value },
                                        }))}
                                        style={{ padding: "6px 8px", borderRadius: 8 }}
                                    >
                                        <option value="">Без муту чату</option>
                                        <option value="3">Мут 3 дні</option>
                                        <option value="7">Мут 7 днів</option>
                                        <option value="14">Мут 14 днів</option>
                                        <option value="30">Мут 30 днів</option>
                                    </select>
                                    <button
                                        type="button"
                                        className="add-btn"
                                        disabled={resolvingComplaintId === c.id}
                                        onClick={() => handleResolveComplaint(c.id, "resolved")}
                                    >
                                        Опрацювати
                                    </button>
                                    <button
                                        type="button"
                                        className="add-btn bg-gray-300 text-black"
                                        disabled={resolvingComplaintId === c.id}
                                        onClick={() => handleResolveComplaint(c.id, "dismissed")}
                                    >
                                        Відхилити
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showInvitesPanel ? (
                <div className="modal-backdrop" onClick={() => setShowInvitesPanel(false)}>
                    <div
                        className="modal-content operator-mobile-modal operator-form-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Інвайт-коди"
                    >
                        <h3>Інвайт-коди для підключення до мережі</h3>
                        <div className="operator-inline-help" style={{ marginBottom: 12 }}>
                            Згенеруй код для мешканця. Клієнт вводить цей код у профілі, створює запит, а далі ти опрацьовуєш його через поточний approve/reject flow.
                        </div>

                        <div className="operator-section-stack">
                            <div className="operator-toolbar-row">
                                <label className="operator-field-inline">
                                    <strong>Діє до</strong>
                                    <input
                                        className="operator-toolbar-input"
                                        type="datetime-local"
                                        value={inviteForm.expires_at}
                                        onChange={(e) => setInviteForm((prev) => ({ ...prev, expires_at: e.target.value }))}
                                    />
                                </label>
                                <div className="operator-inline-actions">
                                    <button type="button" className="add-btn" onClick={handleCreateInvite} disabled={creatingInvite}>
                                        {creatingInvite ? "Створення..." : "Створити інвайт-код"}
                                    </button>
                                    <button
                                        type="button"
                                        className={`add-btn refresh operator-toggle-button ${showArchivedInvites ? "is-open" : ""}`}
                                        onClick={() => setShowArchivedInvites((prev) => !prev)}
                                    >
                                        {showArchivedInvites ? "Сховати архівні" : "Показати архівні"}
                                    </button>
                                </div>
                            </div>

                            <div className="operator-section-stack">
                                {visibleInvites.length > 0 ? visibleInvites.map((invite) => {
                                    const isExpired = invite.expires_at ? new Date(invite.expires_at).getTime() <= Date.now() : false;
                                    return (
                                        <div key={invite.id} className="operator-accordion-item">
                                            <div className="operator-accordion-body" style={{ borderTop: "none", background: "#fff" }}>
                                                <div className="operator-toolbar-row operator-toolbar-row--between">
                                                    <div>
                                                        <div className="operator-accordion-title" style={{ letterSpacing: "0.08em" }}>{invite.code}</div>
                                                        <div className="operator-accordion-meta" style={{ marginTop: 6 }}>
                                                            <span>{invite.network_name || "Мережа"}</span>
                                                            <span>Діє до {invite.expires_at ? new Date(invite.expires_at).toLocaleString("uk-UA") : "-"}</span>
                                                            <span>Статус: {invite.status && !isExpired ? "активний" : isExpired ? "прострочений" : "відкликаний"}</span>
                                                        </div>
                                                    </div>
                                                    <div className="operator-stack-actions">
                                                        <button type="button" className="icon-button gray-btn" onClick={() => handleCopyInviteCode(invite.code)} title="Скопіювати код">
                                                            <Copy size={16} />
                                                        </button>
                                                        {invite.status ? (
                                                            <button type="button" className="icon-button gray-btn" onClick={() => handleRevokeInvite(invite.id)} title="Відкликати код">
                                                                <Trash size={16} />
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }) : <div className="operator-inline-help">Немає інвайт-кодів для поточного режиму відображення.</div>}
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button type="button" className="add-btn cancel-btn" onClick={() => setShowInvitesPanel(false)}>
                                Закрити
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {accessRequests.length > 0 && (
                <div className="message-banner pending-approvals-banner">
                    <div className="pending-approvals-banner__content">
                        <div>
                            <strong>Нові клієнтські запити: {accessRequests.length}</strong>
                            <div style={{ marginTop: 4, fontSize: 13 }}>
                                Клієнти відправили запити на підключення або відключення мережі. Опрацюй їх нижче.
                            </div>
                        </div>
                        <button
                            type="button"
                            className="add-btn"
                            onClick={() => approvalsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        >
                            Перейти до погодження
                        </button>
                    </div>
                </div>
            )}

            {accessRequests.length > 0 && (
                <div className="config-card" style={{ marginBottom: 16 }} ref={approvalsRef}>
                    <h3 style={{ marginTop: 0 }}>Нові запити клієнтів</h3>
                    <div style={{ display: "grid", gap: 12 }}>
                        {accessRequests.map((request) => (
                            <div
                                key={request.id}
                                style={{
                                    border: highlightedAccessRequestId === request.id ? "2px solid #2563eb" : "1px solid #ddd",
                                    borderRadius: 8,
                                    padding: 12,
                                    boxShadow: highlightedAccessRequestId === request.id ? "0 0 0 3px rgba(37, 99, 235, 0.15)" : undefined,
                                    background: highlightedAccessRequestId === request.id ? "#f8fbff" : undefined,
                                }}
                            >
                                <div><strong>{request.client_name}</strong> {request.client_email ? `• ${request.client_email}` : ""}</div>
                                <div style={{ marginTop: 4, fontWeight: 600 }}>
                                    {request.request_type === "disconnect" ? "Запит на відключення мережі" : "Запит на підключення до мережі"}
                                </div>
                                <div style={{ marginTop: 4 }}>
                                    {[request.service_name, request.network_name].filter(Boolean).join(" • ")}
                                </div>
                                {request.descriptions ? <div style={{ marginTop: 4 }}>Коментар: {request.descriptions}</div> : null}
                                {Array.isArray(request.messages) && request.messages.length > 0 ? (
                                    <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                                        {request.messages.map((item) => (
                                            <div
                                                key={item.id}
                                                style={{
                                                    padding: "8px 10px",
                                                    borderRadius: 8,
                                                    background: item.sender_type === "operator" ? "#fff7ed" : "#eff6ff",
                                                }}
                                            >
                                                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                                                    {item.sender_type === "operator" ? "Оператор" : "Клієнт"}
                                                </div>
                                                <div>{item.body}</div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}

                                <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                                    <textarea
                                        placeholder="Уточнення або запитання до клієнта"
                                        rows={3}
                                        value={requestForms[request.id]?.message_body || ""}
                                        onChange={(e) => setRequestForms((prev) => ({
                                            ...prev,
                                            [request.id]: {
                                                ...(prev[request.id] || {}),
                                                message_body: e.target.value,
                                            },
                                        }))}
                                    />
                                    <div>
                                        <button
                                            type="button"
                                            className="add-btn"
                                            onClick={() => handleSendAccessRequestMessage(request.id)}
                                            disabled={sendingRequestMessageId === request.id}
                                        >
                                            {sendingRequestMessageId === request.id ? "Надсилання..." : "Надіслати коментар"}
                                        </button>
                                    </div>
                                    {request.request_type !== "disconnect" ? (
                                        <>
                                            <select
                                                value={requestForms[request.id]?.location_id || ""}
                                                onChange={(e) => setRequestForms((prev) => ({
                                                    ...prev,
                                                    [request.id]: {
                                                        ...(prev[request.id] || {}),
                                                        location_id: e.target.value,
                                                    },
                                                }))}
                                            >
                                                <option value="">Без локації</option>
                                                {locations.map((loc) => (
                                                    <option key={loc.id} value={loc.id}>
                                                        {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                                    </option>
                                                ))}
                                            </select>

                                            <input
                                                type="text"
                                                placeholder="Квартира / секція"
                                                value={requestForms[request.id]?.apartment || ""}
                                                onChange={(e) => setRequestForms((prev) => ({
                                                    ...prev,
                                                    [request.id]: {
                                                        ...(prev[request.id] || {}),
                                                        apartment: e.target.value,
                                                    },
                                                }))}
                                            />

                                            <input
                                                type="text"
                                                placeholder="Особовий рахунок"
                                                value={requestForms[request.id]?.personal_account || ""}
                                                onChange={(e) => setRequestForms((prev) => ({
                                                    ...prev,
                                                    [request.id]: {
                                                        ...(prev[request.id] || {}),
                                                        personal_account: e.target.value,
                                                    },
                                                }))}
                                            />
                                        </>
                                    ) : null}

                                    <input
                                        type="text"
                                        placeholder="Коментар оператору"
                                        value={requestForms[request.id]?.review_comment || ""}
                                        onChange={(e) => setRequestForms((prev) => ({
                                            ...prev,
                                            [request.id]: {
                                                ...(prev[request.id] || {}),
                                                review_comment: e.target.value,
                                            },
                                        }))}
                                    />

                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        <button type="button" className="add-btn" onClick={() => handleApproveAccessRequest(request.id)}>
                                            {request.request_type === "disconnect" ? "Погодити відключення" : "Підтвердити"}
                                        </button>
                                        <button type="button" className="add-btn bg-gray-300 text-black" onClick={() => handleRejectAccessRequest(request.id)}>
                                            {request.request_type === "disconnect" ? "Відхилити відключення" : "Відхилити"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            {/* Модал додавання */}
            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content operator-mobile-modal operator-form-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="mb-2 font-semibold">Додати клієнта</h3>
                        <div className="operator-form-grid">
                            <input placeholder="Ім'я" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} />
                            <input type="email" placeholder="Email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
                            <input placeholder="Телефон" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} />
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <input
                                    style={{ flex: 1, marginBottom: 0 }}
                                    type={showNewClientPassword ? "text" : "password"}
                                    placeholder="Пароль"
                                    value={newClient.password}
                                    onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
                                />
                                <button
                                    type="button"
                                    className="icon-button gray-btn"
                                    onClick={() => setShowNewClientPassword((v) => !v)}
                                    title={showNewClientPassword ? "Приховати пароль" : "Показати пароль"}
                                >
                                    {showNewClientPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            <input className="operator-form-span-2" placeholder="Опис" value={newClient.descriptions} onChange={(e) => setNewClient({ ...newClient, descriptions: e.target.value })} />
                            <input placeholder="Авто" value={newClient.car_number} onChange={(e) => setNewClient({ ...newClient, car_number: e.target.value })} />
                            <input placeholder="Квартира" value={newClient.apartment} onChange={(e) => setNewClient({ ...newClient, apartment: e.target.value })} />
                            <input placeholder="Особовий рахунок" value={newClient.personal_account} onChange={(e) => setNewClient({ ...newClient, personal_account: e.target.value })} />

                            <select value={newClient.location_id} onChange={(e) => setNewClient({ ...newClient, location_id: e.target.value })}>
                                <option value="">Без локації</option>
                                {locations.map((loc) => (
                                    <option key={loc.id} value={loc.id}>
                                        {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAddClient}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">Скасувати</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Мобільна версія */}
            {isMobile ? (
                <div className="operator-accordion-list">
                    {getSortedData().map((client) => {
                        const isEditing = editingId === client.id;
                        const isExpanded = expandedClientCardId === client.id || isEditing;
                        const statusLabel = client.status ? "Активний" : "Неактивний";
                        const locationLabel = getLocationLabelById(client.location_id) || "Без локації";
                        const compactMeta = [
                            statusLabel,
                            locationLabel,
                            client.apartment ? `кв. ${client.apartment}` : null,
                            client.phone || client.email || null,
                        ].filter(Boolean).join(" • ");

                        return (
                        <div key={client.id} className="operator-accordion-item config-card operator-client-card--compact">
                            <button
                                type="button"
                                className="operator-accordion-head"
                                onClick={() => toggleClientCardExpanded(client.id, isEditing)}
                                aria-expanded={isExpanded}
                            >
                                <div className="operator-accordion-head-main">
                                    <div className="operator-accordion-title">{client.name || "Без імені"}</div>
                                    <div className="operator-accordion-meta">{compactMeta}</div>
                                </div>
                                <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                            </button>

                            <OperatorCardActions
                                editing={isEditing}
                                onEdit={() => handleEdit(client)}
                                onSave={() => handleSave(client.id)}
                                onCancel={handleCancelEdit}
                                idleExtras={(
                                    <>
                                        {hasMultiLocation[client.id] && (
                                            <button type="button" className="operator-card-action is-secondary" onClick={() => toggleExpand(client.id)} title="Показати локації">
                                                {expandedClientId === client.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                            </button>
                                        )}
                                        <button type="button" className="operator-card-action is-secondary" onClick={() => handleCopy(client)} title="Додати локацію"><Copy size={16} /></button>
                                    </>
                                )}
                            />

                            {isExpanded ? (
                            <div className="operator-accordion-body">
                            {renderMobileField("Ім’я:", isEditing ? (
                                <input value={edited.name} onChange={(e) => setEdited({ ...edited, name: e.target.value })} />
                            ) : client.name)}

                            {renderMobileField("Email:", isEditing ? (
                                <input value={edited.email} onChange={(e) => setEdited({ ...edited, email: e.target.value })} />
                            ) : client.email)}

                            {renderMobileField("Телефон:", isEditing ? (
                                <input value={edited.phone} onChange={(e) => setEdited({ ...edited, phone: e.target.value })} />
                            ) : client.phone)}

                            {renderMobileField("Пароль:", isEditing ? (
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <input
                                        style={{ flex: 1 }}
                                        type={showEditedPassword ? "text" : "password"}
                                        value={edited.password}
                                        onChange={(e) => setEdited({ ...edited, password: e.target.value })}
                                        placeholder="Новий пароль"
                                    />
                                    <button
                                        type="button"
                                        className="icon-button gray-btn"
                                        onClick={() => setShowEditedPassword((v) => !v)}
                                        title={showEditedPassword ? "Приховати пароль" : "Показати пароль"}
                                    >
                                        {showEditedPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            ) : "••••••")}

                            {renderMobileField("Опис:", isEditing ? (
                                <input value={edited.descriptions} onChange={(e) => setEdited({ ...edited, descriptions: e.target.value })} />
                            ) : (client.descriptions || "-"))}

                            {renderMobileField("Особовий рахунок:", isEditing ? (
                                <input value={edited.personal_account ?? ""} onChange={(e) => setEdited({ ...edited, personal_account: e.target.value })} />
                            ) : (client.personal_account || "-"))}

                            {renderMobileField("Авто:", isEditing ? (
                                <input value={edited.car_number ?? ""} onChange={(e) => setEdited({ ...edited, car_number: e.target.value })} placeholder="Номер авто" />
                            ) : (client.car_number || "-"))}

                            {renderMobileField("Локація:", isEditing ? (
                                <select value={edited.location_id ?? ""} onChange={(e) => setEdited({ ...edited, location_id: e.target.value === "" ? null : parseInt(e.target.value) })}>
                                    <option value="">Без локації</option>
                                    {locations.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                        </option>
                                    ))}
                                </select>
                            ) : (getLocationLabelById(client.location_id) || "Без локації"))}

                            {renderMobileField("Квартира:", isEditing ? (
                                <input value={edited.apartment ?? ""} onChange={(e) => setEdited({ ...edited, apartment: e.target.value })} />
                            ) : (client.apartment || "-"))}

                            {renderMobileField("Верифіковано:", client.is_verified ? "✅ Так" : "❌ Ні")}

                            {renderMobileField("Статус:", isEditing ? (
                                <select value={edited.status} onChange={(e) => setEdited({ ...edited, status: e.target.value === "true" })}>
                                    <option value="true">Активний</option>
                                    <option value="false">Неактивний</option>
                                </select>
                            ) : statusLabel)}

                            {renderMobileField("Створено:", client.created_at ? new Date(client.created_at).toLocaleString("uk-UA") : "-")}

                            <ClientRestrictionPanel clientId={client.id} onChanged={loadBannedClients} />

                            {/* мульти-локації */}
                            {expandedClientId === client.id && (
                                <div className="multi-locations mt-2">
                                    <div className="multi-actions mb-2">
                                        <button onClick={() => handleAddMulti_location(client.id)} className="small-btn">+ Нова локація</button>
                                    </div>

                                    {multiLocations[client.id]?.map((ml) => (
                                        <div key={ml.id} className="multi-card">
                                            <div><strong>Локація:</strong> {editingMultiId === ml.id ? (
                                                <select value={editedMulti.location_id ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, location_id: e.target.value })}>
                                                    <option value="">Без локації</option>
                                                    {locations.map((loc) => (
                                                        <option key={loc.id} value={loc.id}>
                                                            {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (getLocationLabelById(ml.location_id) || "Без локації")}</div>

                                            <div><strong>Опис:</strong> {editingMultiId === ml.id ? (
                                                <input value={editedMulti.descriptions ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, descriptions: e.target.value })} />
                                            ) : ml.descriptions}</div>

                                            <div><strong>Квартира:</strong> {editingMultiId === ml.id ? (
                                                <input value={editedMulti.apartment ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, apartment: e.target.value })} />
                                            ) : ml.apartment || "-"}</div>

                                            <div><strong>Особовий рахунок:</strong> {editingMultiId === ml.id ? (
                                                <input value={editedMulti.personal_account ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, personal_account: e.target.value })} />
                                            ) : ml.personal_account || "-"}</div>

                                            <div><strong>Статус:</strong> {editingMultiId === ml.id ? (
                                                <select value={editedMulti.status} onChange={(e) => setEditedMulti({ ...editedMulti, status: e.target.value === "true" })}>
                                                    <option value="true">Активна</option>
                                                    <option value="false">Неактивна</option>
                                                </select>
                                            ) : ml.status ? "Активна" : "Неактивна"}</div>

                                            <div className={`config-actions flex gap-1 mt-1 ${editingMultiId === ml.id ? "is-editing-inline" : ""}`}>
                                                {editingMultiId === ml.id ? (
                                                    <>
                                                        <button type="button" className="icon-button gray-btn" onClick={() => handleSaveMulti(ml.id, client.id, ml.source)}><Save size={14} /></button>
                                                        <button type="button" className="icon-button gray-btn" onClick={handleCancelMulti}><X size={14} /></button>
                                                        <EditFloatingActions
                                                            onSave={() => handleSaveMulti(ml.id, client.id, ml.source)}
                                                            onCancel={handleCancelMulti}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <button type="button" className="icon-button gray-btn" onClick={() => handleEditMulti(ml)}><Edit size={14} /></button>
                                                        <button type="button" className="icon-button gray-btn" onClick={() => handleDeleteMulti(ml.id, client.id, ml.source)}><Trash size={14} /></button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            </div>
                            ) : null}
                        </div>
                        );
                    })}
                </div>
            ) : (
                /* Desktop */
                <div className="table-responsive operator-clients-table-wrap">
                    <table className={`operator-clients-table${editingId ? " is-editing-row" : ""}`}>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                                    Ім'я {sortBy === "name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("email")}>
                                    Email {sortBy === "email" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th className="clients-col-extra">Пароль</th>
                                <th className="clients-col-extra" style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("location_id")}>
                                    Локація {sortBy === "location_id" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("apartment")}>
                                    Квартира {sortBy === "apartment" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th className="clients-col-extra" style={{ cursor: "pointer" }} onClick={() => toggleSort("personal_account")}>
                                    Особовий рахунок {sortBy === "personal_account" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("car_number")}>
                                    Авто {sortBy === "car_number" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("phone")}>
                                    Телефон {sortBy === "phone" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th className="clients-col-extra">Верифіковано</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th className="clients-col-extra">Створено</th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((client) => (
                                <React.Fragment key={client.id}>
                                    <tr className={editingId === client.id ? "operator-client-row is-editing" : "operator-client-row"}>
                                        <td>{editingId === client.id ? <input value={edited.name} onChange={(e) => setEdited({ ...edited, name: e.target.value })} /> : client.name}</td>
                                        <td>{editingId === client.id ? <input value={edited.email} onChange={(e) => setEdited({ ...edited, email: e.target.value })} /> : client.email}</td>
                                        <td className="clients-col-extra">
                                            {editingId === client.id ? (
                                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <input
                                                        style={{ flex: 1 }}
                                                        type={showEditedPassword ? "text" : "password"}
                                                        value={edited.password}
                                                        onChange={(e) => setEdited({ ...edited, password: e.target.value })}
                                                        placeholder="Новий пароль"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="icon-button"
                                                        onClick={() => setShowEditedPassword((v) => !v)}
                                                        title={showEditedPassword ? "Приховати пароль" : "Показати пароль"}
                                                    >
                                                        {showEditedPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                                    </button>
                                                </div>
                                            ) : (
                                                "••••••"
                                            )}
                                        </td>
                                        <td className="clients-col-extra">{editingId === client.id ? <input value={edited.descriptions} onChange={(e) => setEdited({ ...edited, descriptions: e.target.value })} /> : client.descriptions}</td>
                                        <td>{editingId === client.id ? (
                                            <select value={edited.location_id ?? ""} onChange={(e) => setEdited({ ...edited, location_id: e.target.value === "" ? null : parseInt(e.target.value) })}>
                                                <option value="">Без локації</option>
                                                {locations.map((loc) => (
                                                    <option key={loc.id} value={loc.id}>
                                                        {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (getLocationLabelById(client.location_id) || "Без локації")}</td>
                                        <td>{editingId === client.id ? <input value={edited.apartment ?? ""} onChange={(e) => setEdited({ ...edited, apartment: e.target.value })} /> : client.apartment}</td>
                                        <td className="clients-col-extra">{editingId === client.id ? <input value={edited.personal_account ?? ""} onChange={(e) => setEdited({ ...edited, personal_account: e.target.value })} /> : (client.personal_account || "-")}</td>
                                        <td>{editingId === client.id ? <input value={edited.car_number ?? ""} onChange={(e) => setEdited({ ...edited, car_number: e.target.value })} placeholder="Номер авто" /> : (client.car_number || "-")}</td>
                                        <td>{editingId === client.id ? <input value={edited.phone ?? ""} onChange={(e) => setEdited({ ...edited, phone: e.target.value })} /> : client.phone}</td>
                                        <td className="clients-col-extra">{client.is_verified ? "✅ Так" : "❌ Ні"}</td>
                                        <td>{editingId === client.id ? (
                                            <select value={edited.status} onChange={(e) => setEdited({ ...edited, status: e.target.value === "true" })}>
                                                <option value="true">Активний</option>
                                                <option value="false">Неактивний</option>
                                            </select>
                                        ) : client.status ? "Активний" : "Неактивний"}</td>
                                        <td className="clients-col-extra">{client.created_at ? new Date(client.created_at).toLocaleString() : "-"}</td>
                                        <td className="actions">
                                            {editingId === client.id ? (
                                                <>
                                                    <button className="icon-button" onClick={() => handleSave(client.id)}><Save size={14} /></button>
                                                    <button className="icon-button" onClick={handleCancelEdit}><X size={14} /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button className="icon-button" onClick={() => handleEdit(client)}><Edit size={14} /></button>
                                                    {hasMultiLocation[client.id] && (
                                                        <button className="icon-button" onClick={() => toggleExpand(client.id)}>
                                                            {expandedClientId === client.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                        </button>
                                                    )}
                                                    <button className="icon-button" onClick={() => handleCopy(client)}><Copy size={14} /></button>
                                                    <button
                                                        className="icon-button"
                                                        title="Обмеження"
                                                        onClick={() => setExpandedRestrictionClientId((prev) => (prev === client.id ? null : client.id))}
                                                    >
                                                        🔇
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>

                                    {expandedRestrictionClientId === client.id && (
                                        <tr className="multi-row">
                                            <td colSpan="13">
                                                <ClientRestrictionPanel clientId={client.id} onChanged={loadBannedClients} />
                                            </td>
                                        </tr>
                                    )}

                                    {expandedClientId === client.id && (
                                        <tr className="multi-row">
                                            <td colSpan="13">
                                                <div className="multi-actions mb-2">
                                                    <button onClick={() => handleAddMulti_location(client.id)} className="small-btn">+ Нова локація</button>
                                                </div>

                                                <table className="inner-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Локація</th>
                                                            <th>Опис</th>
                                                            <th>Квартира</th>
                                                            <th>Особовий рахунок</th>
                                                            <th>Статус</th>
                                                            <th>Дії</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {multiLocations[client.id]?.map((ml) => (
                                                            <tr key={ml.id}>
                                                                <td>{editingMultiId === ml.id ? (
                                                                    <select value={editedMulti.location_id ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, location_id: e.target.value })}>
                                                                        <option value="">Без локації</option>
                                                                        {locations.map((loc) => (
                                                                            <option key={loc.id} value={loc.id}>
                                                                                {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : (getLocationLabelById(ml.location_id) || "Без локації")}</td>

                                                                <td>{editingMultiId === ml.id ? <input value={editedMulti.descriptions ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, descriptions: e.target.value })} /> : ml.descriptions}</td>

                                                                <td>{editingMultiId === ml.id ? <input value={editedMulti.apartment ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, apartment: e.target.value })} /> : ml.apartment}</td>

                                                                <td>{editingMultiId === ml.id ? <input value={editedMulti.personal_account ?? ""} onChange={(e) => setEditedMulti({ ...editedMulti, personal_account: e.target.value })} /> : (ml.personal_account || "-")}</td>

                                                                <td>{editingMultiId === ml.id ? (
                                                                    <select value={editedMulti.status} onChange={(e) => setEditedMulti({ ...editedMulti, status: e.target.value === "true" })}>
                                                                        <option value="true">Активна</option>
                                                                        <option value="false">Неактивна</option>
                                                                    </select>
                                                                ) : ml.status ? "Активна" : "Неактивна"}</td>

                                                                <td className="actions">
                                                                    {editingMultiId === ml.id ? (
                                                                        <>
                                                                            <button className="icon-button" onClick={() => handleSaveMulti(ml.id, client.id, ml.source)}><Save size={14} /></button>
                                                                            <button className="icon-button" onClick={handleCancelMulti}><X size={14} /></button>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <button className="icon-button" onClick={() => handleEditMulti(ml)}><Edit size={14} /></button>
                                                                            <button className="icon-button" onClick={() => handleDeleteMulti(ml.id, client.id, ml.source)}><Trash size={14} /></button>
                                                                        </>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )) || <tr><td colSpan="6">Нема мульти-локацій</td></tr>}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ClientsOperators;
